/**
 * Dashboard Page
 *
 * Main dashboard with welcome message, active services, recent invoices.
 * Blueprint Task: 2.2.7
 * GAP_CLOSURE Tier 2-3: Wired to live APIs
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ComplianceHealthScore } from '../components/dashboard/ComplianceHealthScore';
import { DeadlinesWidget } from '../components/dashboard/DeadlinesWidget';
import { ErrorBanner } from '../components/shared/ErrorBanner';
import { AlertIcon, BriefcaseIcon, CurrencyIcon, DocumentIcon, RocketIcon } from '../components/shared/icons';
import { PageHeader } from '../components/shared/PageHeader';
import { DashboardSkeleton } from '../components/shared/skeletons';
import { StatCard } from '../components/shared/StatCard';
import { PORTAL_BRAND } from '../config/branding';
import { getIdToken } from '../lib/auth';
import { useAuth } from '../providers/AuthProvider';
import { getMyOrders, linkGuestOrders } from '../services/orderService';
import {
  getMyServices,
  getServicesNearingRenewal,
  getStateName,
  type ServiceSummary,
} from '../services/servicesService';
import { formatServiceName } from '../utils/serviceName';

import type { OrderSummary } from '../types/order';

const MAGIC_LINK_API_URL = import.meta.env.VITE_MAGIC_LINK_API_URL || 'https://3h28eo2k43.execute-api.us-east-1.amazonaws.com/prod';

interface ActivationInfo {
  orderId: string;
  activationUrl: string;
  serviceName?: string;
}

interface DashboardNotification {
  id: string;
  message: string;
  type: 'warning' | 'info';
}

/**
 *
 */
export function Dashboard() {
  const { user } = useAuth();
  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);
  const [activations, setActivations] = useState<ActivationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchDashboardData() {
      setLoading(true);
      setError(null);

      // Re-attempt guest order linking (catches race condition from signup)
      try { await linkGuestOrders(); } catch { /* non-blocking */ }

      const results = await Promise.allSettled([
        getMyServices(),
        getMyOrders(),
        getServicesNearingRenewal(60),
      ]);

      // Services
      if (results[0].status === 'fulfilled') {
        setServices(results[0].value.services);
      } else {
        console.error('Failed to fetch services:', results[0].reason);
      }

      // Orders
      if (results[1].status === 'fulfilled') {
        setOrders(results[1].value);
      } else {
        console.error('Failed to fetch orders:', results[1].reason);
      }

      // Notifications from renewal data
      if (results[2].status === 'fulfilled') {
        const renewalNotifications: DashboardNotification[] = results[2].value.map((svc, i) => ({
          id: `renewal-${i}`,
          message: `Service for ${svc.entityName} in ${getStateName(svc.state)} renews on ${new Date(svc.renewalDate).toLocaleDateString()}`,
          type: 'warning' as const,
        }));
        setNotifications(renewalNotifications);
      }

      // Only show error if both APIs failed
      if (results[0].status === 'rejected' && results[1].status === 'rejected') {
        setError('Failed to load dashboard data. Please try again.');
      }

      // Detect paid orders without activated services
      const fetchedServices = results[0].status === 'fulfilled' ? results[0].value.services : [];
      const fetchedOrders = results[1].status === 'fulfilled' ? results[1].value : [];
      const paidOrders = fetchedOrders.filter((o: OrderSummary) => o.status === 'paid' || o.status === 'completed');

      if (paidOrders.length > 0 && fetchedServices.length === 0) {
        // The magic-link API only answers the customer whose email owns the order
        const idToken = await getIdToken().catch(() => undefined);
        const activationResults = await Promise.allSettled(
          paidOrders.map(async (order: OrderSummary) => {
            const res = await fetch(
              `${MAGIC_LINK_API_URL}/magic-link/activation-status/${encodeURIComponent(order.orderId)}`,
              { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} }
            );
            if (!res.ok) return null;
            const data = await res.json();
            if (data.ready && data.activationUrl) {
              return {
                orderId: order.orderId,
                activationUrl: data.activationUrl,
                serviceName: formatServiceName(order.primaryService),
              } as ActivationInfo;
            }
            return null;
          })
        );

        const validActivations = activationResults
          .filter((r): r is PromiseFulfilledResult<ActivationInfo | null> => r.status === 'fulfilled')
          .map(r => r.value)
          .filter((a): a is ActivationInfo => a !== null);

        setActivations(validActivations);
      }

      setLoading(false);
    }

    void fetchDashboardData();
  }, []);

  // Computed stats
  const activeServicesCount = services.filter((s) => s.status === 'ACTIVE').length;
  const openInvoicesCount = orders.filter((o) => o.status === 'pending' || o.status === 'processing').length;
  const totalPaid = orders
    .filter((o) => o.status === 'completed' || o.status === 'paid')
    .reduce((sum, o) => sum + o.total, 0);
  const totalPaidFormatted = `$${totalPaid.toFixed(2)}`;

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Welcome section */}
      <PageHeader
        title={`Welcome back, ${user?.name ?? 'there'}!`}
        description="Here's what's happening with your registered agent services."
      />

      {/* Error banner */}
      {error && (
        <ErrorBanner message={error} onRetry={() => window.location.reload()} />
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="space-y-2">
          {notifications.map((notification) => (
            <div key={notification.id} className="rounded-lg border-l-4 border-brand-warning-500 bg-brand-warning-50 p-4">
              <div className="flex">
                <div className="shrink-0">
                  <AlertIcon className="h-5 w-5 text-brand-warning-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-brand-warning-800">
                    {notification.message}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activation CTA cards */}
      {activations.length > 0 && (
        <div className="space-y-3">
          {activations.map((activation) => (
            <div
              key={activation.orderId}
              className="rounded-lg border-2 border-brand-primary-200 bg-brand-primary-50 p-5"
            >
              <div className="flex items-start gap-4">
                <div className="rounded-md bg-brand-primary-100 p-2">
                  <RocketIcon className="h-8 w-8 text-brand-primary-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-brand-primary-900">
                    Your {activation.serviceName || 'Registered Agent Service'} is ready to activate
                  </h3>
                  <p className="mt-1 text-sm text-brand-primary-700">
                    Complete a quick form to start your registered agent service.
                  </p>
                  <a
                    href={activation.activationUrl}
                    className="mt-3 inline-flex items-center rounded-md bg-brand-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-primary-700 transition-colors"
                  >
                    Activate Now
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Expired activation fallback */}
      {activations.length === 0 && services.length === 0 && orders.some(o => o.status === 'paid' || o.status === 'completed') && (
        <div className="rounded-lg border-2 border-brand-warning-200 bg-brand-warning-50 p-5">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-brand-warning-100 p-2">
              <AlertIcon className="h-8 w-8 text-brand-warning-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-brand-warning-900">
                Your activation link has expired
              </h3>
              <p className="mt-1 text-sm text-brand-warning-700">
                Please contact{' '}
                <a href={`mailto:${PORTAL_BRAND.supportEmail}`} className="font-medium underline">
                  {PORTAL_BRAND.supportEmail}
                </a>{' '}
                for assistance.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Active Services"
          value={activeServicesCount.toString()}
          icon={<BriefcaseIcon className="h-6 w-6" />}
        />
        <StatCard
          title="Open Invoices"
          value={openInvoicesCount.toString()}
          icon={<DocumentIcon className="h-6 w-6" />}
        />
        <StatCard
          title="Total Paid"
          value={totalPaidFormatted}
          icon={<CurrencyIcon className="h-6 w-6" />}
        />
      </div>

      {/* Compliance health + Deadlines */}
      <div className="grid gap-6 lg:grid-cols-3">
        <ComplianceHealthScore services={services} />
        <div className="lg:col-span-2">
          <DeadlinesWidget services={services} />
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Active Services */}
        <div className="rounded-lg border border-brand-neutral-200 bg-surface p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-brand-neutral-900">Active Services</h2>
            <Link to="/services" className="text-sm font-medium text-brand-primary-600 hover:text-brand-primary-700">
              View all →
            </Link>
          </div>
          <div className="mt-4 space-y-3">
            {services.map((service) => (
              <div
                key={service.serviceId}
                className="flex items-center justify-between rounded-md border border-brand-neutral-200 p-3"
              >
                <div>
                  <p className="font-medium text-brand-neutral-900">
                    {service.offeringName || `${getStateName(service.state)} Registered Agent`}
                  </p>
                  <p className="text-sm text-brand-neutral-500">
                    {service.entityName} · Renews {new Date(service.renewalDate).toLocaleDateString()}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    service.status === 'ACTIVE'
                      ? 'bg-brand-success-100 text-brand-success-700'
                      : service.status === 'PENDING'
                        ? 'bg-brand-warning-100 text-brand-warning-700'
                        : 'bg-brand-neutral-100 text-brand-neutral-700'
                  }`}
                >
                  {service.status.charAt(0) + service.status.slice(1).toLowerCase()}
                </span>
              </div>
            ))}
            {services.length === 0 && activations.length > 0 && (
              <p className="text-center text-sm text-brand-neutral-500">
                Pending activation — click "Activate Now" above to get started.
              </p>
            )}
            {services.length === 0 && activations.length === 0 && (
              <p className="text-center text-sm text-brand-neutral-500">
                No services found.{' '}
                <Link to="/order" className="text-brand-primary-600 underline hover:text-brand-primary-700">
                  Add one now
                </Link>
              </p>
            )}
          </div>
        </div>

        {/* Recent Invoices (from Orders) */}
        <div className="rounded-lg border border-brand-neutral-200 bg-surface p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-brand-neutral-900">Recent Invoices</h2>
            <Link to="/invoices" className="text-sm font-medium text-brand-primary-600 hover:text-brand-primary-700">
              View all →
            </Link>
          </div>
          <div className="mt-4">
            {orders.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-brand-neutral-500">
                    <th className="pb-2 font-medium">Invoice</th>
                    <th className="pb-2 font-medium">Date</th>
                    <th className="pb-2 font-medium text-right">Amount</th>
                    <th className="pb-2 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {orders.map((order) => {
                    const isPaid = order.status === 'completed' || order.status === 'paid';
                    return (
                      <tr key={order.orderId} className="border-t border-brand-neutral-100">
                        <td className="py-3">
                          <span className="font-medium text-brand-neutral-900">
                            {order.orderId}
                          </span>
                        </td>
                        <td className="py-3 text-brand-neutral-600">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 text-right text-brand-neutral-900">
                          ${order.total.toFixed(2)}
                        </td>
                        <td className="py-3 text-right">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              isPaid
                                ? 'bg-brand-success-100 text-brand-success-700'
                                : 'bg-brand-warning-100 text-brand-warning-700'
                            }`}
                          >
                            {isPaid ? 'Paid' : 'Open'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-center text-sm text-brand-neutral-500">No invoices found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

